import React from "react";
import { toast } from "react-toastify";

import CopyLink from "../../../assets/svg/CopyIcon.svg";
import { digioApi, parseDigioRecord } from "../../../services/digio";
import { getAuthData } from "../../remittance-beta/api/remittanceApi";
import { InlineSpinner } from "../../remittance-beta/components/modal/DigioEsignModal/InlineSpinner";
import { K2DigioRecord } from "../../remittance-beta/types/digio/K2Digio";
import { logApiErrorToSentry } from "../../remittance-beta/utils";
import { buildDigioPayloadFromHistory } from "../utils/digioHistoryEsign";

type DigioLoadingAction = "email" | "sms" | "copy" | "resend" | "changeType" | null;

interface DigioHistoryEsignActionsProps {
  trxnData: any;
  digioRecord: K2DigioRecord;
  onDigioRecordUpdate: (record: K2DigioRecord) => void;
  transactionNumberFallback?: string;
}

export const DigioHistoryEsignActions: React.FC<DigioHistoryEsignActionsProps> = ({
  trxnData,
  digioRecord,
  onDigioRecordUpdate,
  transactionNumberFallback,
}) => {
  const [loadingAction, setLoadingAction] = React.useState<DigioLoadingAction>(null);
  const [isLinkCopied, setIsLinkCopied] = React.useState(false);
  const isLoading = Boolean(loadingAction);
  const uniqueKey = digioRecord.uniqueKey;
  const status = digioRecord.status;
  const currentSendType = digioRecord.sendType === "sms" ? "sms" : "email";
  const oppositeSendType = currentSendType === "email" ? "sms" : "email";
  const digioLink = digioRecord.link || "";

  const getLoggedInUserKey = () => {
    const { userKey } = getAuthData();
    return digioRecord.loggedInUserKey || userKey || "";
  };

  const updateFromResponse = (response: any) => {
    const nextRecord = parseDigioRecord(response);
    if (nextRecord) {
      onDigioRecordUpdate(nextRecord);
    }
    return nextRecord;
  };

  /** DO_LATER only — send link via resume API with full payload */
  const handleResume = async (sendType: "email" | "sms") => {
    if (!uniqueKey || isLoading) {
      return;
    }

    const loggedInUserKey = getLoggedInUserKey();
    if (!loggedInUserKey) {
      toast.error("Unable to send Digio link. Please sign in again.");
      return;
    }

    setLoadingAction(sendType === "email" ? "email" : "sms");

    try {
      const payload = buildDigioPayloadFromHistory(
        trxnData,
        sendType,
        loggedInUserKey,
        digioRecord.transactionNumber || transactionNumberFallback
      );
      const response = await digioApi.resume(uniqueKey, payload);
      updateFromResponse(response);
      toast.success(
        sendType === "email" ? "A2 e-sign link sent on email." : "A2 e-sign link sent on mobile."
      );
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.type || "Unable to send Digio link.");
      logApiErrorToSentry(err, "historyDigioResume");
    } finally {
      setLoadingAction(null);
    }
  };

  /** AWAITING_SIGN — switch email ↔ sms via changeSendType API with full payload */
  const handleChangeType = async () => {
    if (!uniqueKey || isLoading) {
      return;
    }

    const loggedInUserKey = getLoggedInUserKey();
    if (!loggedInUserKey) {
      toast.error("Unable to change Digio send type. Please sign in again.");
      return;
    }

    setLoadingAction("changeType");

    try {
      const payload = buildDigioPayloadFromHistory(
        trxnData,
        oppositeSendType,
        loggedInUserKey,
        digioRecord.transactionNumber || transactionNumberFallback
      );
      const response = await digioApi.changeSendType(uniqueKey, payload);
      updateFromResponse(response);
      toast.success(
        oppositeSendType === "email"
          ? "Send type changed to email."
          : "Send type changed to mobile."
      );
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.type || "Unable to change Digio send type.");
      logApiErrorToSentry(err, "historyDigioChangeSendType");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleResendLink = async () => {
    if (!uniqueKey || isLoading) {
      return;
    }

    setLoadingAction("resend");
    try {
      const response = await digioApi.resendLink(uniqueKey);
      updateFromResponse(response);
      toast.success("A2 e-sign link resent successfully.");
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.type || "Unable to resend Digio link.");
      logApiErrorToSentry(err, "historyDigioResendLink");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleCopyLink = async () => {
    if (!digioLink || isLoading) {
      return;
    }

    setLoadingAction("copy");
    try {
      await navigator.clipboard.writeText(digioLink);
      setIsLinkCopied(true);
      setTimeout(() => setIsLinkCopied(false), 3000);
    } catch (err: any) {
      toast.error("Unable to copy Digio link.");
      logApiErrorToSentry(err, "historyDigioCopyLink");
    } finally {
      setLoadingAction(null);
    }
  };

  if (status === "DO_LATER") {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-1">
          <button
            type="button"
            onClick={() => handleResume("email")}
            disabled={isLoading}
            className="inline-flex items-center gap-1 text-[#0F5BF1] font-semibold disabled:opacity-60"
          >
            email
            {loadingAction === "email" ? <InlineSpinner className="h-4 w-4 text-primary" /> : null}
          </button>
          <span>or</span>
          <button
            type="button"
            onClick={() => handleResume("sms")}
            disabled={isLoading}
            className="inline-flex items-center gap-1 text-[#0F5BF1] font-semibold disabled:opacity-60"
          >
            mobile
            {loadingAction === "sms" ? <InlineSpinner className="h-4 w-4 text-primary" /> : null}
          </button>
        </div>
      </div>
    );
  }

  if (status === "AWAITING_SIGN" || status === "TOKEN_EXPIRED") {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-xs text-[#666]">
          Sent via {currentSendType === "email" ? "email" : "mobile"}. Change to{" "}
          <button
            type="button"
            onClick={handleChangeType}
            disabled={isLoading}
            className="inline-flex items-center gap-1 text-[#0F5BF1] font-semibold disabled:opacity-60"
          >
            {oppositeSendType === "email" ? "email" : "mobile"}
            {loadingAction === "changeType" ? (
              <InlineSpinner className="h-4 w-4 text-primary" />
            ) : null}
          </button>
        </p>

        <div className="flex flex-wrap items-center gap-3">
          {digioLink ? (
            <button
              type="button"
              onClick={handleCopyLink}
              disabled={isLoading}
              className="flex items-center disabled:opacity-60"
            >
              <span
                className={`text-xs font-semibold ${
                  isLinkCopied ? "text-green-500" : "text-[#0F5BF1]"
                }`}
              >
                {isLinkCopied ? "Link Copied" : "Copy Link"}
              </span>
              {loadingAction === "copy" ? (
                <InlineSpinner className="ml-2 h-4 w-4 text-primary" />
              ) : (
                <img className="pl-2" src={CopyLink} alt="copy-link" />
              )}
            </button>
          ) : null}

          <button
            type="button"
            onClick={handleResendLink}
            disabled={isLoading}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#0F5BF1] disabled:opacity-60"
          >
            Resend Link
            {loadingAction === "resend" ? <InlineSpinner className="h-4 w-4 text-primary" /> : null}
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default DigioHistoryEsignActions;
